/* Tổng quan hôm nay — dashboard Tư vấn cơ sở: khách check-in, hàng chờ, phễu chốt, tư vấn viên, nguồn khách, việc cần chú ý */
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { AlertTriangle, CheckCircle2, Clock3, Coins, MessagesSquare, UserCheck, Wallet } from 'lucide-react';
import { Avatar, Bar, Kpi, PRIMARY, money } from '../Telesales/dashboard/ui';
import {
  RESULTS, STAFF_ST, WAIT_WARN_MIN, dayStats, isToday, minutes, overviewOf, payRows, quoteValid, stepDone, vnd,
  type ResultKind, type Visit,
} from './logic';
import { useTuVan } from './store';
import { useBoardRefs } from './receptionApi';
import { Card, Shell, useDoctorView, useNow, who } from './shared';

const WD = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];
const SRC_COLOR: Record<string, string> = { Telesales: 'cyan', 'Đặt lịch': 'geekblue', 'Vãng lai': 'default', 'Khách cũ': 'blue', CSKH: 'purple' };

export default function TongQuanPage() {
  const navigate = useNavigate();
  const now = useNow(30_000);
  // Bác sĩ không xem số tiền (họp 30/10)
  const doctor = useDoctorView();
  const { visits, branchId, staffSt } = useTuVan();
  const ov = useMemo(() => overviewOf(visits, branchId, now), [visits, branchId, now]);
  const today = useMemo(() => visits.filter((v) => v.branchId === branchId && v.status !== 'referral' && v.status !== 'cancelled' && isToday(v.checkInAt ?? v.createdAt, now)), [visits, branchId, now]);
  const branchVisits = useMemo(() => visits.filter((v) => v.branchId === branchId), [visits, branchId]);
  const stats = dayStats(branchVisits, now);
  // Khách cũ quay lại làm tiếp liệu trình chờ bên thực hiện, không tính vào hàng chờ tư vấn
  const waiting = today.filter((v) => v.status === 'waiting' && !v.execOwner);
  const execWait = today.filter((v) => v.status === 'waiting' && v.execOwner);
  const consulting = today.filter((v) => v.status === 'consulting');
  const longest = waiting.reduce((m, v) => Math.max(m, now - (v.checkInAt ?? v.createdAt)), 0);
  const cons = useBoardRefs().consultants;
  const busy = new Set(consulting.map((v) => v.consultant)).size;

  // Việc cần chú ý
  const lateWait = waiting.filter((v) => now - (v.checkInAt ?? v.createdAt) >= WAIT_WARN_MIN * 60_000);
  const unsigned = consulting.filter((v) => v.quote && !quoteValid(v.quote));
  const dueRows = branchVisits.flatMap((v) => payRows(v, now).filter((r) => r.state === 'overdue' || (r.state === 'due_soon' && r.days === 0)).map((r) => ({ v, r })));
  const waitDoctor = consulting.filter((v) => v.invite && !v.quote?.exam.signedAt);
  const notSent = today.filter((v) => v.result && RESULTS[v.result.kind].closed && !stepDone(v, 'exec'));
  const follow = branchVisits.filter((v) => v.result?.callbackAt && isToday(v.result.callbackAt, now));

  const lost = (Object.entries(stats.reasons) as [ResultKind, number][]).filter(([k]) => !RESULTS[k].closed).sort((a, b) => b[1] - a[1]);
  const lostMax = Math.max(1, ...lost.map(([, n]) => n));
  const hourMax = Math.max(1, ...ov.byHour.map((h) => h.n));
  const open = (v: Visit, url = '/phieu-tu-van') => navigate(`${url}?v=${v.id}`);

  return (
    <Shell title="Tổng quan hôm nay" sub={`${WD[dayjs(now).day()]}, ${dayjs(now).format('DD/MM/YYYY')} · khách check-in, hàng chờ, tỉ lệ chốt và việc cần chú ý tại cơ sở`}>
      {/* Chỉ số chính */}
      <div className={`grid grid-cols-2 gap-3 ${doctor ? 'md:grid-cols-4' : 'md:grid-cols-3 xl:grid-cols-6'}`}>
        <Kpi icon={<UserCheck size={15} />} label="Khách check-in" value={ov.checkIn}
          sub={ov.bySource.map((s) => `${s.source} ${s.n}`).join(' · ') || 'Chưa có khách'} />
        <Kpi icon={<Clock3 size={15} />} label="Đang chờ" value={waiting.length} tone={lateWait.length ? 'red' : 'amber'} alert={lateWait.length > 0}
          onClick={() => navigate('/app-tuvan')}
          sub={<>{waiting.length ? <>Chờ lâu nhất <b>{minutes(longest)} phút</b></> : `Chờ TB ${ov.avgWait} phút đến khi được nhận`}{execWait.length ? ` · ${execWait.length} khách chờ làm liệu trình` : ''}</>}
          hint={`Tô đỏ khi có khách chờ từ ${WAIT_WARN_MIN} phút chưa được nhận`} />
        <Kpi icon={<MessagesSquare size={15} />} label="Đang tư vấn" value={consulting.length} tone="sky" onClick={() => navigate('/khach-tu-van')}
          sub={consulting.length ? `${busy}/${cons.length} tư vấn viên đang bận${waitDoctor.length ? ` · ${waitDoctor.length} chờ bác sĩ` : ''}` : 'Chưa có'} />
        <Kpi icon={<CheckCircle2 size={15} />} label="Tỉ lệ chốt" value={`${stats.rate}%`} tone="green"
          sub={<>Chốt <b>{stats.closed}</b> / {stats.consulted} khách đã ghi kết quả</>}
          hint="Khách chốt (thanh toán hoặc cọc) / khách đã ghi kết quả hôm nay" />
        {!doctor && <>
        <Kpi icon={<Coins size={15} />} label="Doanh số chốt" value={money(stats.revenue)} tone="teal"
          sub="Theo phiếu báo giá chính thức của khách đã chốt" />
        <Kpi icon={<Wallet size={15} />} label="Quầy đã thu" value={money(ov.collected)} tone="indigo" onClick={() => navigate('/thanh-toan-dot')}
          sub={dueRows.length ? <span className="text-red-500">{dueRows.length} đợt quá hạn / đến hạn hôm nay</span> : 'Không có đợt quá hạn'} />
        </>}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-3">
        {/* Phễu */}
        <Card title="Phễu tư vấn hôm nay" className="xl:col-span-2" extra={<span className="text-xs text-gray-500">% so với khách check-in</span>}>
          <div className="space-y-2.5">
            {ov.funnel.map((f, i) => {
              const pct = ov.checkIn ? Math.round((f.n / ov.checkIn) * 100) : 0;
              const prev = i ? ov.funnel[i - 1].n : 0;
              return (
                <Tooltip key={f.key} title={i ? `${f.n}/${prev} khách bước trước đi tiếp (${prev ? Math.round((f.n / prev) * 100) : 0}%)` : `${f.n} khách check-in hôm nay`}>
                  <div className="grid grid-cols-[170px_1fr_90px] items-center gap-3 text-[13px]">
                    <span className="truncate text-gray-600 dark:text-gray-300">{f.label}</span>
                    <div className="h-6 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-700">
                      <div className="h-full rounded-md" style={{ width: `${pct}%`, background: PRIMARY, opacity: 1 - i * 0.1 }} />
                    </div>
                    <span className="text-right tabular-nums"><b className="text-gray-800 dark:text-gray-100">{f.n}</b> <span className="text-xs text-gray-500">· {pct}%</span></span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </Card>

        {/* Việc cần chú ý */}
        <Card title={<span className="flex items-center gap-1.5"><AlertTriangle size={15} className="text-amber-500" />Cần chú ý</span>}>
          <div className="space-y-3 text-[13px]">
            <Attention label={`Khách chờ quá ${WAIT_WARN_MIN} phút`} items={lateWait.map((v) => ({ key: v.id, text: v.cust.name, sub: `${minutes(now - (v.checkInAt ?? v.createdAt))} phút`, onClick: () => navigate('/app-tuvan') }))} />
            <Attention label="Chờ bác sĩ khám" items={waitDoctor.map((v) => ({ key: v.id, text: v.cust.name, sub: `mời ${minutes(now - v.invite!.at)} phút`, onClick: () => open(v, '/bac-si-kham') }))} />
            <Attention label="Báo giá chưa có chữ ký bác sĩ" items={unsigned.map((v) => ({ key: v.id, text: v.cust.name, sub: who(v.consultant), onClick: () => open(v, '/phieu-bao-gia') }))} />
            <Attention label="Đã chốt, chưa chuyển thực hiện" items={notSent.map((v) => ({ key: v.id, text: v.cust.name, sub: who(v.consultant), onClick: () => open(v, '/chuyen-thuc-hien') }))} />
            {!doctor && <Attention label="Đợt thanh toán quá hạn / đến hạn hôm nay" items={dueRows.map(({ v, r }) => ({ key: `${v.id}-${r.no}`, text: `${v.cust.name} · đợt ${r.no}`, sub: vnd(r.amount - r.paid), onClick: () => open(v, '/coc-tai-co-so') }))} />}
            <Attention label="Khách hẹn trả lời hôm nay" items={follow.map((v) => ({ key: v.id, text: v.cust.name, sub: dayjs(v.result!.callbackAt).format('HH:mm'), onClick: () => open(v, '/ket-qua-tu-van') }))} />
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-3">
        {/* Tư vấn viên */}
        <Card title="Tư vấn viên hôm nay" className="xl:col-span-2">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase text-gray-500">
                  <th className="pb-2 font-semibold">Tư vấn viên</th><th className="pb-2 font-semibold">Đang làm</th>
                  <th className="pb-2 text-right font-semibold">Lượt</th><th className="pb-2 text-right font-semibold">Chốt</th>
                  <th className="w-[140px] pb-2 pl-4 font-semibold">Tỉ lệ chốt</th>{!doctor && <th className="pb-2 text-right font-semibold">Doanh số</th>}
                </tr>
              </thead>
              <tbody>
                {cons.map((c) => {
                  const st = staffSt[c.id] ?? 'ready';
                  const mine = today.filter((v) => v.consultant === c.id);
                  const cur = mine.find((v) => v.status === 'consulting');
                  // Được chia khách nhưng chưa bấm nhận
                  const pend = waiting.find((v) => v.consultant === c.id);
                  const s = dayStats(branchVisits, now, c.id);
                  return (
                    <tr key={c.id} className="border-t border-gray-100 dark:border-gray-700">
                      <td className="py-2">
                        <span className="flex items-center gap-2">
                          <Avatar name={c.name} color={c.color} size={24} />
                          <span><span className="font-semibold text-gray-800 dark:text-gray-100">{c.name}</span>
                            <span className="ml-1.5 text-xs" style={{ color: STAFF_ST[st].dot }}>● {STAFF_ST[st].label}</span></span>
                        </span>
                      </td>
                      <td className="py-2 text-gray-600 dark:text-gray-300">
                        {cur ? <button type="button" className="text-[#3e91ab] hover:underline" onClick={() => open(cur)}>{cur.cust.name}{cur.room ? ` · ${cur.room}` : ''}</button> : pend ? <button type="button" className="text-amber-600 hover:underline" onClick={() => navigate('/app-tuvan')}>Được chia {pend.cust.name} · chờ nhận {minutes(now - (pend.assignedAt ?? now))} phút</button>
                          : <span className="text-gray-400">Rảnh</span>}
                      </td>
                      <td className="py-2 text-right tabular-nums">{mine.length}</td>
                      <td className="py-2 text-right tabular-nums">{s.closed}/{s.consulted}</td>
                      <td className="py-2 pl-4"><span className="flex items-center gap-2"><Bar value={s.rate} color="#22c55e" /><span className="w-9 text-right text-xs tabular-nums">{s.rate}%</span></span></td>
                      {!doctor && <td className="py-2 text-right font-semibold tabular-nums">{money(s.revenue)}</td>}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Nguồn khách */}
        <Card title="Theo nguồn khách">
          <div className="space-y-3">
            {ov.bySource.length ? ov.bySource.map((s) => (
              <div key={s.source} className="text-[13px]">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <Tag color={SRC_COLOR[s.source]} className="!m-0">{s.source}</Tag>
                  <span className="text-xs text-gray-500"><b className="text-gray-800 dark:text-gray-100">{s.n}</b> khách · chốt {s.closed}/{s.done}{s.done ? ` (${Math.round((s.closed / s.done) * 100)}%)` : ''}</span>
                </div>
                <Bar value={s.n} max={ov.checkIn} />
              </div>
            )) : <div className="py-6 text-center text-[13px] text-gray-400">Chưa có khách</div>}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 xl:grid-cols-3">
        {/* Check-in theo giờ */}
        <Card title="Khách check-in theo giờ" className="xl:col-span-2">
          <div className="flex h-[150px] items-end gap-1.5">
            {ov.byHour.map((h) => {
              const nowHour = dayjs(now).hour() === h.hour;
              return (
                <Tooltip key={h.hour} title={`${h.hour}:00–${h.hour + 1}:00 · ${h.n} khách`}>
                  <div className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                    <span className="text-[11px] tabular-nums text-gray-500">{h.n || ''}</span>
                    <div className="w-full rounded-t-[4px]" style={{ height: `${(h.n / hourMax) * 100}%`, minHeight: h.n ? 4 : 0, background: nowHour ? '#f59e0b' : PRIMARY }} />
                    <span className={`text-[11px] ${nowHour ? 'font-bold text-amber-600' : 'text-gray-500'}`}>{h.hour}h</span>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        </Card>

        {/* Lý do chưa chốt */}
        <Card title="Lý do chưa chốt">
          <div className="space-y-2.5">
            {lost.length ? lost.map(([k, n]) => (
              <div key={k} className="text-[13px]">
                <div className="mb-1 flex justify-between gap-2"><span className="text-gray-600 dark:text-gray-300">{RESULTS[k].short}</span><b>{n}</b></div>
                <Bar value={n} max={lostMax} color="#f59e0b" />
              </div>
            )) : <div className="py-6 text-center text-[13px] text-gray-400">Chưa có khách chưa chốt</div>}
          </div>
        </Card>
      </div>
    </Shell>
  );
}

function Attention({ label, items }: { label: string; items: { key: string; text: string; sub?: string; onClick: () => void }[] }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-xs font-semibold text-gray-500">
        <span>{label}</span>
        <span className={items.length ? 'text-red-500' : 'text-green-600'}>{items.length || '✓'}</span>
      </div>
      {items.slice(0, 4).map((x) => (
        <button key={x.key} type="button" onClick={x.onClick} className="flex w-full items-center justify-between gap-2 rounded px-1 py-0.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40">
          <span className="truncate text-[#3e91ab]">{x.text}</span>{x.sub && <span className="shrink-0 text-xs text-gray-500">{x.sub}</span>}
        </button>
      ))}
      {items.length > 4 && <div className="px-1 text-xs text-gray-400">+{items.length - 4} khách khác</div>}
    </div>
  );
}
